import React, { createContext, useState, useContext, useEffect } from 'react';

interface User {
    name: string;
}

interface AuthContextType {
    isAuthenticated: boolean;
    user: User | null;
    companyName: string | null;
    isLoading: boolean;
    login: (accessToken: string, refreshToken: string | null, user: User, companyName: string) => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
        return !!localStorage.getItem('token');
    });
    
    const [user, setUser] = useState<User | null>(() => {
        const storedUser = localStorage.getItem('user');
        return storedUser ? JSON.parse(storedUser) : null;
    });
    
    const [companyName, setCompanyName] = useState<string | null>(() => {
        return localStorage.getItem('companyName');
    });
    
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        const syncFromStorage = () => {
const token = localStorage.getItem("accessToken") ?? localStorage.getItem("token") ?? "";
            const storedUser = localStorage.getItem('user');
            const storedCompany = localStorage.getItem('companyName');

            if (token && storedUser && storedCompany) {
                setIsAuthenticated(true);
                setUser(JSON.parse(storedUser));
                setCompanyName(storedCompany);
                console.log("User restored from localStorage:", JSON.parse(storedUser));
                console.log("Company restored from localStorage:", storedCompany);
            } else {
                setIsAuthenticated(false);
                setUser(null);
                setCompanyName(null);
            }
            
            setIsLoading(false);
        };

        syncFromStorage();

        const handleStorageChange = () => syncFromStorage();
        const handleAuthChange = () => syncFromStorage();

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('auth-change', handleAuthChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('auth-change', handleAuthChange);
        };
    }, []);

    const login = (accessToken: string, refreshToken: string | null, user: User, companyName: string) => {
        localStorage.setItem('token', accessToken);
        if (refreshToken) {
            localStorage.setItem('refreshToken', refreshToken);
        }
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('companyName', companyName);
        
        setIsAuthenticated(true);
        setUser(user);
        setCompanyName(companyName);
        
        console.log("Login successful!");
        console.log("User:", user);
        console.log("Company Name:", companyName);
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('companyName');
        localStorage.removeItem('openMenu');
        
        setIsAuthenticated(false);
        setUser(null);
        setCompanyName(null);
        
        console.log("Logout successful!");
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, user, companyName, isLoading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
